import { useEffect } from 'react';

/**
 * Registers a `beforeunload` event listener that shows the browser's native
 * "Leave site?" dialog whenever `isDirty` is true.
 *
 * The hook is a no-op when `isDirty` is false, so it adds no overhead during
 * normal browsing. It cleans up automatically when the component unmounts.
 *
 * @example
 * function EditorShell() {
 *   const isDirty = useIsDirty();
 *   useBeforeUnload(isDirty);
 *   // ...
 * }
 */
export function useBeforeUnload(isDirty: boolean): void {
  useEffect(() => {
    if (!isDirty) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent): void => {
      // Modern browsers require returnValue to be set to trigger the dialog.
      event.preventDefault();
      // Legacy support (some older browsers read returnValue as the prompt text)
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isDirty]);
}
